import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Stack, Chip, CircularProgress,
    Input, IconButton, Divider,
} from '@mui/joy';
import { motion } from 'framer-motion';

import ArrowBackRoundedIcon              from '@mui/icons-material/ArrowBackRounded';
import AccountBalanceWalletRoundedIcon   from '@mui/icons-material/AccountBalanceWalletRounded';
import PaidRoundedIcon                   from '@mui/icons-material/PaidRounded';
import EmojiEventsRoundedIcon            from '@mui/icons-material/EmojiEventsRounded';
import RemoveCircleOutlineRoundedIcon    from '@mui/icons-material/RemoveCircleOutlineRounded';
import WorkRoundedIcon                   from '@mui/icons-material/WorkRounded';
import CalendarTodayRoundedIcon          from '@mui/icons-material/CalendarTodayRounded';
import CreditCardRoundedIcon             from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon               from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon            from '@mui/icons-material/PointOfSaleRounded';
import TrendingUpRoundedIcon             from '@mui/icons-material/TrendingUpRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import { APIGetMyStats } from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtMoney = (val) => {
    const n = parseFloat(val) || 0;
    return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

const getMonthStart = () => {
    const now = new Date();
    return toISO(new Date(now.getFullYear(), now.getMonth(), 1));
};

const getPeriods = () => {
    const now = new Date();
    const today = toISO(now);
    const yesterday = new Date(now); yesterday.setDate(yesterday.getDate() - 1);
    const weekStart = new Date(now);
    const day = weekStart.getDay();
    weekStart.setDate(weekStart.getDate() - (day === 0 ? 6 : day - 1));
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    return [
        { key: 'today',     label: 'Сегодня',  from: today,              to: today },
        { key: 'yesterday', label: 'Вчера',    from: toISO(yesterday),   to: toISO(yesterday) },
        { key: 'week',      label: 'Неделя',   from: toISO(weekStart),   to: today },
        { key: 'month',     label: 'Месяц',    from: toISO(monthStart),  to: today },
    ];
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white', flex: 1,
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
    "&:hover": {
        bgcolor: 'var(--joy-palette-background-level3)', 
    }
};


// ════════════════════════════════════════════════════════════════════
//  RoleBlock — секция роли с разбивкой по точкам
// ════════════════════════════════════════════════════════════════════
function RoleBlock({ title, role, data, accent }) {
    const isPh = role === 'photographer';
    const total      = parseFloat(isPh ? data.total : data.subordinate_total) || 0;
    const salary     = parseFloat(data.salary) || 0;
    const reward     = parseFloat(data.reward) || 0;
    const daily      = parseFloat(data.daily_reward || 0);
    const fines      = parseFloat(data.fines) || 0;
    const deductions = parseFloat(data.deductions) || 0;
    const net        = parseFloat(data.net_pay) || 0;

    // Защита от случайных дублей по location_id + сортировка по выручке
    const locations = useMemo(() => {
        const map = new Map();
        for (const loc of (data.by_location || [])) {
            if (!map.has(loc.location_id)) map.set(loc.location_id, loc);
        }
        return Array.from(map.values()).sort((a, b) => {
            const ta = parseFloat(isPh ? a.total : a.subordinate_total) || 0;
            const tb = parseFloat(isPh ? b.total : b.subordinate_total) || 0;
            return tb - ta;
        });
    }, [data.by_location, isPh]);

    return (
        <motion.div variants={fadeUp}>
            <Box sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                overflow: 'hidden',
            }}>
                {/* Шапка секции */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 2, py: 1.5, borderBottom: '1px solid var(--joy-palette-divider)' }}>
                    <Box sx={{ width: 4, height: 20, bgcolor: accent, borderRadius: 1 }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'var(--joy-palette-text-primary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {title}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: net >= 0 ? '#22c55e' : 'var(--joy-palette-danger-400)', fontVariantNumeric: 'tabular-nums' }}>
                        {net >= 0 ? '' : '−'}{fmtMoney(Math.abs(net))} ₽
                    </Typography>
                </Box>

                {/* Суммарная сетка по роли */}
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: locations.length ? 2 : 0 }}>
                        <Box sx={{ px: 1.5, py: 1.2, borderRadius: 'var(--joy-radius-md)', bgcolor: 'var(--joy-palette-background-level2)' }}>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>Выручка</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-primary-300)' }}>{fmtMoney(total)}</Typography>
                        </Box>
                        <Box sx={{ px: 1.5, py: 1.2, borderRadius: 'var(--joy-palette-radius-md)', bgcolor: 'var(--joy-palette-background-level2)' }}>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>{isPh ? 'ЗП' : 'Оклад'}</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#818cf8' }}>{fmtMoney(salary)}</Typography>
                        </Box>
                        <Box sx={{ px: 1.5, py: 1.2, borderRadius: 'var(--joy-radius-md)', bgcolor: 'var(--joy-palette-background-level2)' }}>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>Премии {isPh && '+ дн.'}</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#22c55e' }}>{fmtMoney(reward + daily)}</Typography>
                        </Box>
                        <Box sx={{ px: 1.5, py: 1.2, borderRadius: 'var(--joy-radius-md)', bgcolor: 'var(--joy-palette-background-level2)' }}>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>Штрафы / Списания</Typography>
                            <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-danger-400)' }}>
                                {(fines + deductions) > 0 ? '−' : ''}{fmtMoney(fines + deductions)}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Разбивка по точкам */}
                    {locations.length > 0 && (
                        <>
                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1, fontWeight: 700 }}>
                                По точкам
                            </Typography>
                            <Stack spacing={1}>
                                {locations.map(loc => {
                                    const locTotal = parseFloat(isPh ? loc.total : loc.subordinate_total) || 0;
                                    const locNet   = parseFloat(loc.net_pay) || 0;
                                    return (
                                        <Box key={loc.location_id} sx={{
                                            px: 1.5, py: 1.2,
                                            borderRadius: 'var(--joy-radius-md)',
                                            bgcolor: 'var(--joy-palette-background-level2)',
                                            border: '1px solid var(--joy-palette-divider)',
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                    {loc.location_name}
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: locNet >= 0 ? '#22c55e' : 'var(--joy-palette-danger-400)', fontVariantNumeric: 'tabular-nums' }}>
                                                    {locNet >= 0 ? '' : '−'}{fmtMoney(Math.abs(locNet))} ₽
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', gap: 1.2, fontSize: 10, flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    Выручка <span style={{ color: 'var(--joy-palette-primary-300)', fontWeight: 700 }}>{fmtMoney(locTotal)}</span>
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    {isPh ? 'ЗП' : 'Оклад'} <span style={{ color: '#818cf8', fontWeight: 700 }}>{fmtMoney(loc.salary)}</span>
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    Премии <span style={{ color: '#22c55e', fontWeight: 700 }}>{fmtMoney(parseFloat(loc.reward) + parseFloat(loc.daily_reward || 0))}</span>
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    Дни <span style={{ color: 'var(--joy-palette-text-secondary)', fontWeight: 700 }}>{loc.work_days || 0}</span>
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </>
                    )}
                </Box>
            </Box>
        </motion.div>
    );
}


// ════════════════════════════════════════════════════════════════════
//  MySalaryPage — личная статистика фотографа
// ════════════════════════════════════════════════════════════════════
export default function MySalaryPage() {
    const { token, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();
    const periods = useMemo(getPeriods, []);

    const [data,         setData]         = useState(null);
    const [loading,      setLoading]      = useState(true);
    const [activePeriod, setActivePeriod] = useState('month');
    const [dateFrom,     setDateFrom]     = useState(getMonthStart);
    const [dateTo,       setDateTo]       = useState(() => toISO(new Date()));

    const loadStats = useCallback(async () => {
        setLoading(true);
        const res = await APIGetMyStats(token, { date_from: dateFrom, date_to: dateTo });
        if (res && !res.error) setData(res);
        setLoading(false);
    }, [token, dateFrom, dateTo]);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => onRefreshTick(loadStats), [onRefreshTick, loadStats]);

    const selectPeriod = (p) => { setActivePeriod(p.key); setDateFrom(p.from); setDateTo(p.to); };

    const s  = data?.stats || {};
    const ph = s.as_photographer || null;
    const ad = s.as_administrator || null;
    const combinedNet = parseFloat(s.combined_net_pay) || 0;
    const hasData = ph || ad;
    const cash       = parseFloat(s.cash) || 0;
    const terminal   = parseFloat(s.terminal) || 0;
    const salary     = parseFloat(s.salary) || 0;
    const reward     = parseFloat(s.reward) || 0;
    const dailyReward = parseFloat(s.daily_reward) || 0;
    const fines      = parseFloat(s.fines) || 0;
    const deductions = parseFloat(s.deductions) || 0;
    const netPay     = parseFloat(s.net_pay) || 0;
    const workDays   = s.work_days || 0;

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}>
            <Header />

            <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ─────────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <IconButton variant="plain" onClick={() => navigate('/')}
                        sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                            Моя зарплата
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            Статистика и расчёт
                        </Typography>
                    </Box>
                </Box>

                {/* ── Чипы периодов ────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', gap: 0.8, mb: 1.5, flexWrap: 'wrap' }}>
                        {periods.map(p => (
                            <Chip key={p.key} size="sm"
                                variant={activePeriod === p.key ? 'solid' : 'outlined'}
                                color={activePeriod === p.key ? 'primary' : 'neutral'}
                                onClick={() => selectPeriod(p)}
                                sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 12, px: 2, py: 0.5, 
                                    ...inputSx,
                                    "&:hover": {
                                        color: activePeriod === p.key ? 'white' : 'black'
                                    }
                                }}
                            >
                                {p.label}
                            </Chip>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
                        <Input type="date" size="sm" value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setActivePeriod(''); }}
                            sx={inputSx} />
                        <Input type="date" size="sm" value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setActivePeriod(''); }}
                            sx={inputSx} />
                    </Box>
                </motion.div>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2}>

                            {/* ── Главная карточка: к выплате ────── */}
                            <motion.div variants={fadeUp}>
                                <Box sx={{
                                    px: 2.5, py: 2.5,
                                    borderRadius: 'var(--joy-radius-xl)',
                                    border: '1px solid var(--joy-palette-primary-500)',
                                    bgcolor: 'rgba(162,38,226,0.08)',
                                    textAlign: 'center',
                                }}>
                                    <WorkRoundedIcon sx={{ fontSize: 28, color: 'var(--joy-palette-primary-400)', mb: 0.5 }} />
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em' }}>
                                        Итого к выплате
                                    </Typography>
                                    <Typography sx={{
                                        fontSize: 38, fontWeight: 800, lineHeight: 1.1, mt: 0.5,
                                        fontVariantNumeric: 'tabular-nums',
                                        color: combinedNet >= 0 ? '#22c55e' : 'var(--joy-palette-danger-400)',
                                    }}>
                                        {combinedNet >= 0 ? '' : '−'}{fmtMoney(Math.abs(combinedNet))} ₽
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 1, flexWrap: 'wrap' }}>
                                        {ph && (
                                            <Chip size="sm" variant="soft" sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e', fontSize: 10, fontWeight: 700 }}>
                                                Фотограф: {fmtMoney(parseFloat(ph.net_pay) || 0)} ₽
                                            </Chip>
                                        )}
                                        {ad && (
                                            <Chip size="sm" variant="soft" sx={{ bgcolor: 'rgba(129,140,248,0.15)', color: '#818cf8', fontSize: 10, fontWeight: 700 }}>
                                                Админ: {fmtMoney(parseFloat(ad.net_pay) || 0)} ₽
                                            </Chip>
                                        )}
                                    </Box>
                                </Box>
                            </motion.div>

                            {/* ── Секция: Как фотограф ─────────────────── */}
                            {ph && <RoleBlock title="Как фотограф" role="photographer" data={ph} accent="#22c55e" />}

                            {/* ── Секция: Как администратор ────────────── */}
                            {ad && <RoleBlock title="Как администратор" role="administrator" data={ad} accent="#818cf8" />}

                            {!hasData && (
                                <motion.div variants={fadeUp}>
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                                            Нет данных за выбранный период
                                        </Typography>
                                    </Box>
                                </motion.div>
                            )}

                        </Stack>
                    </motion.div>
                )}
            </Box>
        </motion.div>
    );
}